import { Alert, Checkbox, Divider, FormControl, FormControlLabel, FormLabel, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageRadioButtons from './LanguageRadioButton';
import { useFormik } from 'formik';
import i18n from 'i18n';
import { InterestsCheckbox } from 'components/InterestsCheckbox';
import FormButtons from '../Buttons/FormButtons';
import { NewsLetterCheckbox } from 'components/NewsLetterCheckbox';
import GetContentTypeResponse from 'services/newsletter/GetContentTypeResponse';
import UpdateContentTypePayload from 'services/newsletter/UpdateContentTypePayload';
import { useConsentUserInfo } from '../ConsentPreferenceBase';
import { SetConsentValues } from 'pages/preferences/ConsentValue';
import { UpdateContentType } from 'services/newsletter/NewsLetterService';
import { noChoice } from 'common/FLSConst';

const PreferenceCenterForm = (props: {
  ContentTypeInfo: GetContentTypeResponse;
  setErrorMessage: any;
  isLoading: boolean;
  setIsLoading: any;
}): JSX.Element => {
  const { t } = useTranslation('component');
  const [saveMessage, setSaveMessage] = useState('');
  const { consentUserInfo } = useConsentUserInfo();

  const initialValues = {
    language: props.ContentTypeInfo
      ? props.ContentTypeInfo.Language == 'French'
        ? 'fr'
        : 'en'
      : i18n.resolvedLanguage,
    profilingConsent: props.ContentTypeInfo?.ProfilingConsent ?? false,
    interestsChecked: props.ContentTypeInfo?.Interests.map((a) => a.Id.toString()) ?? [],
    newsletterTypes: props.ContentTypeInfo?.NewsLetterType.map((a) => a.Id.toString()) ?? [],
  };

  const handleSave = async (values: any) => {
    if (!props.isLoading) {
      props.setIsLoading(true);
      setSaveMessage('');
      props.setErrorMessage('');

      let interests = values.interestsChecked;
      if (interests.length == 0) {
        interests = noChoice;
      }

      const updatedContentTypePayload: UpdateContentTypePayload = {
        Language: values.language,
        SmsConsent: null,
        ProfilingConsent: SetConsentValues(values.profilingConsent, props.ContentTypeInfo.ProfilingConsent),
        InterestIds: interests,
        NewsLetterType: values.newsletterTypes,
        Email: consentUserInfo.Email ?? '',
        Identifier: consentUserInfo.Identifier ?? '',
      };

      try {
        await UpdateContentType(updatedContentTypePayload);
        props.ContentTypeInfo.ProfilingConsent = values.profilingConsent;

        setSaveMessage('Preferences.SaveSuccess');
      } catch (e) {
        props.setErrorMessage('Errors.GenericError');
      } finally {
        props.setIsLoading(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values: any) => {
      handleSave(values);
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {saveMessage && (
        <Alert severity="success" sx={{ mb: '16px' }}>
          {t(saveMessage)}
        </Alert>
      )}
      <LanguageRadioButtons formik={formik} showOptional={false} />
      <Divider />
      <NewsLetterCheckbox
        disableContentTypes={false}
        formik={formik}
        setError={props.setErrorMessage}
        showPtsBox={false}
        showOnlyNewsletterContent={true}
      />
      <Divider />
      <FormControl>
        <FormLabel>{t('CommunicationPreferences.personalizationTitle')}</FormLabel>
        <FormControlLabel
          control={
            <Checkbox id="profilingConsent" onChange={formik.handleChange} checked={formik.values.profilingConsent} />
          }
          label={
            <Typography variant="body2">
              {t('LegalTitle.ReadAndAgree') + ' ' + t('Prepositions.That') + ' ' + t('LegalTitle.Data')}
            </Typography>
          }
        />
      </FormControl>
      <Divider />
      <InterestsCheckbox formik={formik} showPtsBox={false} setError={props.setErrorMessage} />
      <Divider />
      <FormButtons
        SubmitButtonText={t('Buttons.Save')}
        PrevButtonHandler={false}
        isLoading={props.isLoading}
        PrevButtonText={''}
      />
    </form>
  );
};

export default PreferenceCenterForm;
